import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import { BottomTabBar } from "@react-navigation/bottom-tabs";
import { getFocusedRouteNameFromRoute } from "@react-navigation/native";
import { Tabs, usePathname, useRouter, useSegments } from "expo-router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Animated,
  BackHandler,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

import { CustomHeader } from "@/components/CustomHeader";
import { HapticTab } from "@/components/HapticTab";
import Sidebar from "@/components/Sidebar";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { APP_HOME_ROUTE, resolveHomeTabScreen } from "@/constants/AppNavigation";
import { Colors } from "@/constants/Colors";
import Feather from "@expo/vector-icons/Feather";
import { useAuth } from "@/context/AuthContext";
import { PostsProvider } from "@/context/PostsContext";
import { ToastProvider } from "@/context/ToastContext";
import { useColorScheme } from "@/hooks/useColorScheme";
import {
  collection,
  doc,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { SafeAreaView } from "react-native-safe-area-context";

import {
  ScrollHideProvider,
  useScrollHideProgress,
} from "@/context/ScrollHideContext";
import { firestore } from "@/firebase/web";
import { shouldShowPlateGuidePrompt } from "@/utils/onboarding.web";

let plateGuideSidebarAutoOpenedForUserId: string | null = null;
const plateGuideSidebarVisibleStorageKey = "plateGuideSidebarVisible";
const getPersistedSidebarVisibility = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return (
    window.sessionStorage.getItem(plateGuideSidebarVisibleStorageKey) === "1"
  );
};
let plateGuideSidebarVisiblePersisted = getPersistedSidebarVisibility();

const AnimatedTabBar = (props: React.ComponentProps<typeof BottomTabBar>) => {
  const progress = useScrollHideProgress();
  const { t } = useTranslation();
  const opacity = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0.4],
  });
  return (
    <Animated.View
      role="navigation"
      aria-label={t("NAV_PRIMARY")}
      style={{ opacity }}
    >
      <BottomTabBar {...props} />
    </Animated.View>
  );
};

const UnreadBadge = ({ count }: { count: number }) => {
  if (count === 0) return null;
  return (
    <ThemedView
      style={{
        position: "absolute",
        right: -5,
        top: -5,
        backgroundColor: "red",
        borderRadius: 10,
        width: 20,
        height: 20,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ThemedText style={{ color: "white", fontSize: 12 }}>{count}</ThemedText>
    </ThemedView>
  );
};

export default function TabLayout() {
  const { user, userData, isLoading } = useAuth();
  const { t } = useTranslation();
  const colorScheme = useColorScheme();
  const [isSidebarVisible, setSidebarVisible] = useState(
    plateGuideSidebarVisiblePersisted,
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const [groupInvitationCount, setGroupInvitationCount] = useState(0);
  const [showPlateGuidePrompt, setShowPlateGuidePrompt] = useState(false);
  const [highlightSettingsEntry, setHighlightSettingsEntry] = useState(false);
  const autoOpenSidebarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const sidebarCloseBlockedUntilRef = useRef(0);

  const router = useRouter();
  const pathname = usePathname();

  const setSidebarVisibilityPersisted = useCallback((nextVisible: boolean) => {
    plateGuideSidebarVisiblePersisted = nextVisible;
    if (typeof window !== "undefined") {
      if (nextVisible) {
        window.sessionStorage.setItem(plateGuideSidebarVisibleStorageKey, "1");
      } else {
        window.sessionStorage.removeItem(plateGuideSidebarVisibleStorageKey);
      }
    }
    setSidebarVisible(nextVisible);
  }, []);

  const openSidebarWithCloseGuard = useCallback(() => {
    sidebarCloseBlockedUntilRef.current = Date.now() + 700;
    setSidebarVisibilityPersisted(true);
  }, [setSidebarVisibilityPersisted]);

  const handleSidebarClose = () => {
    if (Date.now() < sidebarCloseBlockedUntilRef.current) {
      return;
    }
    setSidebarVisibilityPersisted(false);
  };

  useEffect(() => {
    if (user) {
      const notificationsRef = collection(
        doc(collection(firestore, "users"), user.uid),
        "notifications",
      );
      const q = query(notificationsRef, where("read", "==", false));
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          setUnreadCount(snapshot.size);
        },
        (error) => {
          console.error("Notifications listener permission error:", error);
          setUnreadCount(0);
        },
      );
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const groupInvitationsRef = collection(firestore, "groupInvitations");
      const q = query(
        groupInvitationsRef,
        where("inviteeUid", "==", user.uid),
        where("status", "==", "pending"),
      );
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          setGroupInvitationCount(snapshot.size);
        },
        (error) => {
          console.error(
            "Group invitation badge listener permission error:",
            error,
          );
          setGroupInvitationCount(0);
        },
      );
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | null = null;

    if (!user) {
      if (mounted) setShowPlateGuidePrompt(false);
      return;
    }

    const userDocRef = doc(firestore, "users", user.uid);
    unsubscribe = onSnapshot(
      userDocRef,
      { includeMetadataChanges: true },
      async (userDocSnap) => {
        if (!mounted) return;

        try {
          const userData = userDocSnap.exists() ? userDocSnap.data() : null;
          if (mounted) {
            setShowPlateGuidePrompt(shouldShowPlateGuidePrompt(userData));
          }
        } catch (error) {
          console.error("Error evaluating plate guide prompt:", error);
        }
      },
    );

    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (Platform.OS === "android") {
      const onBackPress = () => {
        if (pathname !== "/") {
          router.replace("/");
          return true;
        }
        return false;
      };
      const subscription = BackHandler.addEventListener(
        "hardwareBackPress",
        onBackPress,
      );
      return () => subscription.remove();
    }
  }, [pathname, router]);

  useEffect(() => {
    if (!user?.uid) {
      plateGuideSidebarAutoOpenedForUserId = null;
      plateGuideSidebarVisiblePersisted = false;
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem(plateGuideSidebarVisibleStorageKey);
      }
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!showPlateGuidePrompt || !user?.uid) {
      return;
    }

    if (plateGuideSidebarAutoOpenedForUserId === user.uid) {
      setHighlightSettingsEntry(true);
      openSidebarWithCloseGuard();
      return;
    }

    if (autoOpenSidebarTimerRef.current) {
      clearTimeout(autoOpenSidebarTimerRef.current);
    }

    autoOpenSidebarTimerRef.current = setTimeout(() => {
      plateGuideSidebarAutoOpenedForUserId = user.uid;
      setHighlightSettingsEntry(true);
      openSidebarWithCloseGuard();
      autoOpenSidebarTimerRef.current = null;
    }, 120);

    return () => {
      if (autoOpenSidebarTimerRef.current) {
        clearTimeout(autoOpenSidebarTimerRef.current);
        autoOpenSidebarTimerRef.current = null;
      }
    };
  }, [showPlateGuidePrompt, user?.uid, openSidebarWithCloseGuard]);

  const segments = useSegments() as string[];

  const isPublicPath =
    pathname === "/" ||
    pathname === "/index" ||
    pathname.includes("public-profile") ||
    segments.length === 0 ||
    (segments.length === 1 && segments[0] === "(tabs)") ||
    segments.includes("index") ||
    segments.includes("public-profile");

  useEffect(() => {
    if (!isLoading && !user && !isPublicPath) {
      router.replace("/(auth)/login" as never);
    }
  }, [isLoading, user, isPublicPath, router]);

  if (isLoading || (!user && !isPublicPath)) {
    return null;
  }

  // Wait for the Firestore user doc before mounting the Tabs navigator so
  // `initialRouteName` is picked from the user's defaultHomeTab preference,
  // not the "feed" fallback.
  if (user && !userData) {
    return null;
  }

  const handleOpenPlateGuide = () => {
    setSidebarVisibilityPersisted(false);
    handleSettingsEntryNavigate();
  };

  const handleSettingsEntryNavigate = () => {
    setShowPlateGuidePrompt(false);
    setHighlightSettingsEntry(false);
    router.push("/(tabs)/settings?plateTour=1&step=settings" as never);
  };

  return (
    <ActionSheetProvider>
      <ToastProvider>
        <PostsProvider>
          <ScrollHideProvider>
            <SafeAreaView style={{ flex: 1 }} edges={["top", "bottom"]}>
              <Tabs
                tabBar={(props) => <AnimatedTabBar {...props} />}
                initialRouteName={resolveHomeTabScreen(userData?.defaultHomeTab)}
                screenOptions={{
                tabBarShowLabel: false,
                tabBarActiveTintColor:
                  Colors[(colorScheme ?? "light") as keyof typeof Colors].tabIconActive,
                tabBarInactiveTintColor:
                  Colors[(colorScheme ?? "light") as keyof typeof Colors].tabIconInactive,
                headerShown: false,
                tabBarButton: HapticTab,
                tabBarItemStyle: {
                  paddingBottom: 6,
                  paddingTop: 2,
                },
                tabBarStyle: {
                  height: 40,
                  borderTopWidth: 0,
                  backgroundColor:
                    Colors[(colorScheme ?? "light") as keyof typeof Colors].tabBarBackground,
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
              }}
              backBehavior="history"
            >
              <Tabs.Screen
                name="profile"
                options={{
                  tabBarIcon: ({ color }) => (
                    <Feather size={24} name="user" color={color} />
                  ),
                  tabBarStyle: {
                    display: "none",
                  },
                }}
                listeners={{
                  tabPress: (e: any) => {
                    e.preventDefault();
                    if (!user) {
                      router.replace("/(auth)/login" as never);
                      return;
                    }
                    openSidebarWithCloseGuard();
                  },
                }}
              />
              <Tabs.Screen
                name="groups"
                options={({ route }) => {
                  // Hide on groups sub-flows (`group-info`, `create`, `[id]`,
                  // `invite`). See _layout.tsx for the rationale — must OMIT
                  // the tabBarStyle key, not set it to undefined, or the
                  // parent screenOptions' position:absolute style gets
                  // clobbered.
                  const hide = [
                    "group-info",
                    "create",
                    "[id]",
                    "invite",
                  ].includes(getFocusedRouteNameFromRoute(route) ?? "");
                  return {
                    tabBarIcon: ({ color }) => (
                      <View>
                        <Feather size={24} name="users" color={color} />
                        <UnreadBadge count={groupInvitationCount} />
                      </View>
                    ),
                    ...(hide ? { tabBarStyle: { display: "none" } } : {}),
                  };
                }}
                listeners={{
                  tabPress: (e: any) => {
                    e.preventDefault();
                    if (!user) {
                      router.replace("/(auth)/login" as never);
                      return;
                    }
                    if (pathname.includes("groups")) {
                      return;
                    }
                    router.replace(APP_HOME_ROUTE as never);
                  },
                }}
              />
              <Tabs.Screen
                name="index"
                options={{
                  tabBarIcon: ({ color }) => (
                    <Feather size={24} name="globe" color={color} />
                  ),
                  tabBarButtonTestID: "tab-feed",
                }}
              />
              <Tabs.Screen
                name="search"
                options={{
                  tabBarIcon: ({ color }) => (
                    <Feather size={24} name="search" color={color} />
                  ),
                  tabBarButtonTestID: "tab-search",
                }}
                listeners={{
                  tabPress: (e: any) => {
                    if (!user) {
                      e.preventDefault();
                      router.replace("/(auth)/login" as never);
                    }
                  },
                }}
              />
              <Tabs.Screen
                name="notifications"
                options={{
                  headerShown: true,
                  header: () => (
                    <CustomHeader
                      title={t("NOTIFICATIONS")}
                      type="subtitle"
                      variant="tab"
                    />
                  ),
                  tabBarIcon: ({ color }) => (
                    <View>
                      <Feather size={24} name="bell" color={color} />
                      <UnreadBadge count={unreadCount} />
                    </View>
                  ),
                }}
                listeners={{
                  tabPress: (e: any) => {
                    if (!user) {
                      e.preventDefault();
                      router.replace("/(auth)/login" as never);
                    }
                  },
                }}
              />
              <Tabs.Screen
                name="settings"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="settings-plates"
                options={{
                  href: null,
                  tabBarItemStyle: {
                    display: "none",
                  },
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="settings-notifications"
                options={{
                  href: null,
                  tabBarItemStyle: {
                    display: "none",
                  },
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="settings-signin-methods"
                options={{
                  href: null,
                  tabBarItemStyle: {
                    display: "none",
                  },
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="settings-default-home"
                options={{
                  href: null,
                  tabBarItemStyle: {
                    display: "none",
                  },
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="help"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="post/[postId]"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                  headerShown: false,
                }}
              />
              <Tabs.Screen
                name="post/new"
                options={{
                  title: "Post",
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="post/edit/[postId]"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                  headerShown: false,
                }}
              />
              <Tabs.Screen
                name="report/area"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="public-profile"
                options={{
                  href: null,
                  headerShown: false,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
            </Tabs>
            <Sidebar
              isVisible={isSidebarVisible}
              onClose={handleSidebarClose}
              highlightSettingsOption={highlightSettingsEntry}
              onHighlightedSettingsPress={handleSettingsEntryNavigate}
            />
            {showPlateGuidePrompt && (
              <ThemedView style={styles.plateGuidePromptContainer}>
                <ThemedText style={styles.plateGuidePromptTitle}>
                  {t("PLATE_GUIDE_PROMPT_TITLE")}
                </ThemedText>
                <ThemedText style={styles.plateGuidePromptText}>
                  {t("PLATE_GUIDE_PROMPT_MESSAGE")}
                </ThemedText>
                <ThemedView style={styles.plateGuidePromptActions}>
                  <TouchableOpacity
                    onPress={async (event) => {
                      event.stopPropagation();
                      setShowPlateGuidePrompt(false);
                      setHighlightSettingsEntry(false);
                      setSidebarVisibilityPersisted(false);
                      if (user) {
                        try {
                          await setDoc(
                            doc(firestore, "users", user.uid),
                            { "onboardingPrefs.plateTourCompleted": true },
                            { merge: true },
                          );
                        } catch (error) {
                          console.error(
                            "Error dismissing plate guide prompt:",
                            error,
                          );
                        }
                      }
                    }}
                  >
                    <ThemedText type="link">{t("NOT_NOW")}</ThemedText>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={(event) => {
                      event.stopPropagation();
                      handleOpenPlateGuide();
                    }}
                  >
                    <ThemedText type="link">
                      {t("ONBOARDING_CONTINUE")}
                    </ThemedText>
                  </TouchableOpacity>
                </ThemedView>
              </ThemedView>
            )}
            </SafeAreaView>
          </ScrollHideProvider>
        </PostsProvider>
      </ToastProvider>
    </ActionSheetProvider>
  );
}

const styles = StyleSheet.create({
  plateGuidePromptContainer: {
    position: "absolute",
    right: 16,
    bottom: 16,
    zIndex: 9999,
    width: 320,
    borderRadius: 12,
    backgroundColor: "#fff",
    padding: 12,
    borderWidth: 1,
    borderColor: "#d9d9d9",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 100,
    gap: 8,
  },
  plateGuidePromptTitle: {
    fontSize: 14,
    fontWeight: "700",
  },
  plateGuidePromptText: {
    fontSize: 13,
    opacity: 0.85,
  },
  plateGuidePromptActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
});
